import { useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { materialCategories } from "@/mocks/materials";

interface AdminUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    file: File,
    metadata: { title: string; description: string; category: string; category_label: string }
  ) => Promise<void>;
}

export default function AdminUpload({ isOpen, onClose, onUpload }: AdminUploadProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("beginner");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categoryLabel = materialCategories.find((c) => c.key === category)?.label || "입문";

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("beginner");
    setFile(null);
    setUploadError(null);
    setUploadSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setUploadError(null);
      if (!title) {
        const nameWithoutExt = selected.name.replace(/\.[^/.]+$/, "");
        setTitle(nameWithoutExt);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      setFile(dropped);
      setUploadError(null);
      if (!title) {
        const nameWithoutExt = dropped.name.replace(/\.[^/.]+$/, "");
        setTitle(nameWithoutExt);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setUploadError("파일을 선택해주세요");
      return;
    }
    if (!title.trim()) {
      setUploadError("자료 제목을 입력해주세요");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      await onUpload(file, {
        title: title.trim(),
        description: description.trim(),
        category,
        category_label: categoryLabel,
      });
      setUploadSuccess(true);
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "업로드 중 오류가 발생했습니다");
    } finally {
      setUploading(false);
    }
  };

  const fileIcon = file
    ? file.name.endsWith(".pdf")
      ? "ri-file-pdf-line text-red-500"
      : file.name.endsWith(".ppt") || file.name.endsWith(".pptx")
      ? "ri-file-ppt-line text-orange-500"
      : "ri-file-line text-[#8C8780]"
    : "ri-upload-cloud-2-line text-[#C4956A]";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" as const }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#E8E5E0]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-[#C4956A]"></i>
                </div>
                <h2 className="text-base font-bold text-[#1C1917]">강의자료 등록</h2>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-lg hover:bg-[#F5F0EC] flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-[#8C8780]"></i>
              </button>
            </div>

            {/* Success State */}
            {uploadSuccess ? (
              <div className="p-10 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center"
                >
                  <i className="ri-check-line text-3xl text-green-500"></i>
                </motion.div>
                <h3 className="text-lg font-bold text-[#1C1917] mb-1">등록 완료!</h3>
                <p className="text-sm text-[#8C8780]">강의자료가 성공적으로 등록되었습니다</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* File Upload Area */}
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    파일 선택 <span className="text-red-400">*</span>
                  </label>
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      dragOver
                        ? "border-[#C4956A] bg-[#C4956A]/5"
                        : file
                        ? "border-green-300 bg-green-50/50"
                        : "border-[#E0DCD6] hover:border-[#C4956A]/40 hover:bg-[#FAF8F6]"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.ppt,.pptx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    {file ? (
                      <div className="flex flex-col items-center gap-2">
                        <i className={`text-3xl ${fileIcon}`}></i>
                        <p className="text-sm font-medium text-[#1C1917]">{file.name}</p>
                        <p className="text-xs text-[#8C8780]">
                          {(file.size / (1024 * 1024)).toFixed(1)}MB
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                          }}
                          className="text-xs text-red-400 hover:text-red-500 transition-colors mt-1"
                        >
                          파일 변경
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-[#C4956A]/10 flex items-center justify-center">
                          <i className="ri-upload-cloud-2-line text-xl text-[#C4956A]"></i>
                        </div>
                        <p className="text-sm font-medium text-[#5C5650]">
                          파일을 드래그하거나 클릭하여 선택
                        </p>
                        <p className="text-xs text-[#B8B3AE]">PDF, PPT 파일만 가능 (최대 50MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    자료 제목 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="예: 경매기초반 1강 - 경매일반 & 권리분석"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    자료 설명
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="자료에 대한 간단한 설명을 입력하세요"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors resize-none"
                  />
                  <p className="text-xs text-[#B8B3AE] mt-1 text-right">{description.length}/500</p>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    카테고리
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {materialCategories.filter((c) => c.key !== "all").map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => setCategory(cat.key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                          category === cat.key
                            ? "bg-[#C4956A] text-white"
                            : "bg-white border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A]"
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Error */}
                {uploadError && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                  >
                    {uploadError}
                  </motion.div>
                )}

                {/* Submit */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-2.5 text-sm font-medium border border-[#E8E5E0] rounded-full text-[#5C5650] hover:bg-[#F5F0EC] transition-all whitespace-nowrap"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || !file || !title.trim()}
                    className="flex-1 py-2.5 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-2-line"></i>
                        등록하기
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}